/**
 * Secure Authentication Context
 * Provides authentication state and methods without exposing Firebase credentials
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { secureAuthService, User } from '../services/secureAuthService';
import { signInWithPopup, GoogleAuthProvider, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const SecureAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const currentUser = secureAuthService.getCurrentUser();
        const isAuth = secureAuthService.isAuthenticated();
        
        if (isAuth && currentUser) {
          // Verify token is still valid
          const isValid = await secureAuthService.verifyToken();
          if (isValid) {
            setUser(currentUser);
            setIsAuthenticated(true);
          } else {
            secureAuthService.logout();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await secureAuthService.login(email, password);
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string, displayName: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await secureAuthService.register(email, password, displayName);
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Call backend logout endpoint to log session (fire-and-forget, don't block logout)
      const token = localStorage.getItem('authToken');
      if (token) {
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {
          // Silent failure - don't block logout
        });
      }
    } catch (error) {
      // Silent failure - don't block logout
    }
    
    // Clear local auth state
    secureAuthService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  const signInWithGoogle = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      
      // Get the Firebase ID token
      const idToken = await result.user.getIdToken();
      // Convert Firebase user to our User interface
      const user: User = {
        uid: result.user.uid,
        email: result.user.email || '',
        displayName: result.user.displayName || '',
        photoURL: result.user.photoURL || undefined
      };
      
      // Store the token and user in secureAuthService
      secureAuthService.storeFirebaseAuth(idToken, user);
      
      setUser(user);
      setIsAuthenticated(true);
      
      } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
      } catch (error) {
      throw error;
    }
  };

  const verifyToken = async (): Promise<boolean> => {
    try {
      const isValid = await secureAuthService.verifyToken();
      if (isValid) {
        const currentUser = secureAuthService.getCurrentUser();
        setUser(currentUser);
        setIsAuthenticated(true);
        return true;
      } else {
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    signInWithGoogle,
    resetPassword,
    logout,
    verifyToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useSecureAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSecureAuth must be used within a SecureAuthProvider');
  }
  return context;
};

export default AuthContext;
